/**
 * User section settings document schemas and Firebase paths.
 *
 * Defines the Firestore schema for per-user, per-course section preferences.
 * Shared between the webapp and other packages.
 */

import {z} from 'zod';

import type {CourseId} from '../../../types/branded';

// ============================================================================
// Schema
// ============================================================================

/**
 * Schema for a user's per-course section settings document.
 *
 * Uses `.loose()` to allow additional Firestore metadata fields.
 */
export const SectionSettingsDocumentSchema = z
  .object({
    /** Whether the user has opted out of LLM-generated section feedback. */
    noLlmSectionFeedback: z.boolean().optional(),
  })
  .loose();

/**
 * A user's section settings for a specific course.
 *
 * Stored at: `users/{userId}/{courseId}/sectionSettings`
 *
 * The document is course-scoped so that (a) preferences can differ per-course
 * and (b) Firestore rules can gate cross-user reads on course membership /
 * teaching-team role.
 */
export type SectionSettingsDocument = z.infer<
  typeof SectionSettingsDocumentSchema
>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Constructs the path to a user's section settings document for a specific
 * course.
 *
 * Document path: `users/{userId}/{courseId}/sectionSettings`
 *
 * @param userId - The target user whose settings doc is addressed.
 * @param courseId - The course whose settings are being read/written.
 * @returns The fully-qualified Firestore document path.
 */
export function getSectionSettingsDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: CourseId;
}): string {
  return `users/${userId}/${courseId}/sectionSettings`;
}
